/**
 * Measuring the echo: did this reply reproduce the opponent's phrasing?
 *
 * WHY N-GRAMS AND NOT WORD OVERLAP. Two people arguing about soil temperature
 * will both say "soil", "germination" and "damping-off", and a bag-of-words
 * metric reads that shared vocabulary as copying. It is not; it is the topic.
 * `voiceDivergence` already learned this the expensive way: it measured
 * vocabulary while the question was about register, passed its own self-tests,
 * and could not answer the thing it was built for.
 *
 * A shared run of five consecutive words is different in kind. Nobody arrives
 * independently at "slightly elevated temperature undoubtedly accelerates
 * germination". Five-word shingles measure PHRASING, which is the actual
 * complaint.
 *
 * WHAT IT IS FOR. The staged debate's rebuttal round produced this pair, one
 * turn apart, and the second speaker was quoting the first speaker's attack on
 * the second speaker:
 *
 *   Gardener:    "The Hermeticist's assessment reveals a fundamental
 *                 misunderstanding of the biological processes involved..."
 *   Hermeticist: "The Hermeticist's assessment reveals a fundamental
 *                 misunderstanding of the underlying biological processes..."
 *
 * The detector has to separate that from genuine engagement, where one side
 * picks up the other's TERM ("numerical target") and argues against it. Those
 * are opposite outcomes and they look similar to a careless metric, so the tests
 * pin both directions against real transcript text rather than invented text.
 */

/**
 * Words, lowercased, punctuation removed.
 *
 * The curly forms are not optional. Models write U+2019 and U+201C, and a
 * previous detector in this codebase reported "clean" on output that plainly
 * was not, because it only knew the straight apostrophe.
 */
export function words(text: string): string[] {
  return (
    text
      .toLowerCase()
      // Curly APOSTROPHES become straight ones, so "soil's" is one token.
      .replace(/[‘’]/g, "'")
      // Curly DOUBLE quotes become separators. Folding them to an apostrophe
      // instead is a bug the tests caught: it turned a quoted word into
      // "'disruptions" while its straight-quoted twin became "disruptions", so
      // two identical sentences scored zero against each other.
      .replace(/[“”]/g, " ")
      .replace(/[^\p{L}\p{N}'\s]+/gu, " ")
      .split(/\s+/)
      // An apostrophe is only part of a word in the middle of one.
      .map((w) => w.replace(/^'+|'+$/g, ""))
      .filter(Boolean)
  );
}

export function shingles(text: string, n = 5): Set<string> {
  const w = words(text);
  const out = new Set<string>();
  for (let i = 0; i + n <= w.length; i++) out.add(w.slice(i, i + n).join(" "));
  return out;
}

/**
 * Fraction of this reply's phrasing that already existed in theirs.
 *
 * Directional on purpose. "How much of what I said was theirs" is the question;
 * the reverse is a different question and mixing them would let a long original
 * reply hide a wholesale copy inside it.
 *
 * Zero for a reply too short to contain a single shingle, because a metric that
 * returns 1.0 on an empty string would score silence as perfect plagiarism and
 * silence as perfect originality depending on which way it rounded.
 */
export function echoScore(reply: string, theirs: string, n = 5): number {
  const mine = shingles(reply, n);
  if (mine.size === 0) return 0;
  const other = shingles(theirs, n);
  let hit = 0;
  for (const s of mine) if (other.has(s)) hit++;
  return hit / mine.size;
}

/**
 * The longest unbroken run of words this reply took from theirs, and WHERE it
 * ends, so the run itself can be recovered rather than only its length.
 *
 * Classic longest-common-substring over token arrays, one row at a time so a
 * long transcript does not allocate a full matrix.
 */
function longestRun(reply: string, theirs: string): { len: number; endA: number; a: string[] } {
  const a = words(reply);
  const b = words(theirs);
  const seen = new Map<string, number[]>();
  for (let j = 0; j < b.length; j++) {
    const list = seen.get(b[j]);
    if (list) list.push(j);
    else seen.set(b[j], [j]);
  }
  let best = 0;
  let endA = -1;
  let prev = new Int32Array(b.length + 1);
  for (let i = 0; i < a.length; i++) {
    const cur = new Int32Array(b.length + 1);
    for (const j of seen.get(a[i]) ?? []) {
      cur[j + 1] = prev[j] + 1;
      if (cur[j + 1] > best) {
        best = cur[j + 1];
        endA = i;
      }
    }
    prev = cur;
  }
  return { len: best, endA, a };
}

/** The longest unbroken run of words this reply took from theirs. */
export function longestSharedRun(reply: string, theirs: string): number {
  return longestRun(reply, theirs).len;
}

/**
 * The shared run itself, as text.
 *
 * A report that says `run=15` cannot be read. It cannot tell a copied clause
 * from a technical term two experts on the same subject were always going to
 * share, and deciding which one it is has so far meant going back to the raw
 * transcript by hand. Every threshold in this file was calibrated by looking at
 * the actual words, so the actual words belong in the output.
 *
 * Normalised, not verbatim: these are the tokens the metric compared, which is
 * what a reader needs in order to judge whether the metric was right.
 */
export function sharedRunText(reply: string, theirs: string): string {
  const { len, endA, a } = longestRun(reply, theirs);
  if (len === 0 || endA < 0) return "";
  return a.slice(endA - len + 1, endA + 1).join(" ");
}

/**
 * Words that carry no content, dropped before the paraphrase check.
 *
 * Function words appear in the same order in any two English sentences, so
 * leaving them in would give every pair of paragraphs a high sequence score and
 * the metric would report nothing at all.
 */
const STOP = new Set([
  "a", "an", "the", "and", "or", "but", "if", "of", "to", "in", "on", "at", "by", "for", "with",
  "from", "as", "is", "are", "was", "were", "be", "been", "being", "it", "its", "this", "that",
  "these", "those", "there", "their", "them", "they", "he", "she", "we", "you", "i", "not", "no",
  "can", "will", "would", "could", "should", "may", "might", "must", "do", "does", "did", "has",
  "have", "had", "than", "then", "so", "such", "into", "within", "while", "which", "who", "what",
  "when", "where", "how", "why", "more", "most", "much", "very", "also", "both", "each", "any",
  "all", "one", "two", "s", "t",
]);

const contentWords = (text: string) => words(text).filter((w) => !STOP.has(w));

/**
 * Longest common SUBSEQUENCE: shared order, gaps allowed.
 *
 * The difference from `longestSharedRun` is the whole point. A run demands
 * adjacency and a paraphrase breaks adjacency at every synonym. Order survives.
 */
function lcsLength(a: string[], b: string[]): number {
  if (a.length === 0 || b.length === 0) return 0;
  let prev = new Int32Array(b.length + 1);
  for (let i = 0; i < a.length; i++) {
    const cur = new Int32Array(b.length + 1);
    for (let j = 0; j < b.length; j++) {
      cur[j + 1] = a[i] === b[j] ? prev[j] + 1 : Math.max(cur[j], prev[j + 1]);
    }
    prev = cur;
  }
  return prev[b.length];
}

/**
 * Did this reply follow their SENTENCE, with the words swapped?
 *
 * WHY THIS EXISTS, and it is the eighth instrument failure in this project.
 * Free ran the first collaboration and one speaker's private note came back as
 * an obvious paraphrase of the other's last turn. `echoScore` reported 0.147,
 * under its 0.25 threshold, and the longest shared run was five words:
 *
 *   theirs  "a precisely engineered, layered mesh - dramatically IMPROVES print
 *            bed adhesion and stability, EFFECTIVELY COUNTERACTING localized
 *            deformation and REDUCING THE FORMATION OF flickering layers"
 *   note    "a precisely engineered, layered mesh - drastically IMPROVE print
 *            bed adhesion and stability, COUNTERING localized deformation and
 *            MINIMIZING flickering layers"
 *
 * Every five-word shingle is broken by a synonym while the sentence is taken
 * whole. Lowering the shingle threshold cannot fix it: genuine engagement sits
 * at 0.04, so 0.147 is only 3.7x above the noise, against 13x for real
 * copying. The gap the threshold was calibrated on does not exist here.
 *
 * What a paraphrase cannot avoid is ORDER. It walks the same content words in
 * the same sequence, because it is rewriting a sentence rather than composing
 * one. Directional like `echoScore`, and for the same reason: the question is
 * how much of MY sequence came from THEIRS.
 */
export function paraphraseScore(reply: string, theirs: string): number {
  const a = contentWords(reply);
  if (a.length < 8) return 0; // too short for order to mean anything
  return lcsLength(a, contentWords(theirs)) / a.length;
}

/**
 * Above this, a reply has followed their sentence rather than written its own.
 *
 * Calibrated against the same real fixtures as `ECHO_THRESHOLD`, and the
 * separation is what makes it usable rather than the roundness of the number.
 * See `echoScore.test.mjs`, which pins every one of these.
 */
export const PARAPHRASE_THRESHOLD = 0.5;

/**
 * Above this, a reply is reproducing rather than replying.
 *
 * Calibrated against real transcript, not chosen for roundness: measured
 * copying in the failing runs sits at 0.45 and above, while genuine engagement
 * on the same topic and the same question sits below 0.05. The gap is an order
 * of magnitude, so the exact threshold is not load-bearing and anything in the
 * middle deserves a human read rather than a verdict.
 */
export const ECHO_THRESHOLD = 0.25;

/**
 * A run this long is a copied SENTENCE, whatever the ratio says.
 *
 * Exists because a single lifted sentence inside an otherwise original long
 * reply gets diluted by the ratio and would slip through.
 *
 * Calibrated on real transcript, and it was wrong the first time. Set to 8, it
 * flagged "and the overall stability of the soil profile" as an echo: a topical
 * noun phrase two speakers reached independently because they were both talking
 * about soil, scoring 0.04 overall. Measured copying runs 13 words and scores
 * 0.538. Twelve sits in that gap and asks for a whole clause, not a phrase.
 */
export const RUN_THRESHOLD = 12;

export type EchoVerdict = {
  score: number;
  longestRun: number;
  /** Shared word ORDER, which is what survives a synonym swap. */
  paraphrase: number;
  echoed: boolean;
};

/**
 * Three ways to take someone's words, and it took three measurements to learn
 * that one test cannot see all of them:
 *
 *   verbatim     shingles catch it        0.551 echo
 *   paraphrase   ONLY order catches it    0.147 echo, 0.710 paraphrase
 *   template     openerShape catches it   0.000 echo
 *
 * Measured separation on real transcript, both directions:
 *
 *   verbatim copy            para 0.912
 *   paraphrased note         para 0.710, 0.638
 *   genuine engagement       para 0.077
 *   independent openings     para 0.040
 *
 * The threshold sits in that gap rather than at a round number, and it is set
 * toward the copying end on purpose: a false positive here would accuse a
 * collaborator of copying for agreeing, which is the one thing collaboration is
 * supposed to allow.
 */
export function judgeEcho(reply: string, theirs: string): EchoVerdict {
  const score = echoScore(reply, theirs);
  const longestRun = longestSharedRun(reply, theirs);
  const paraphrase = paraphraseScore(reply, theirs);
  return {
    score,
    longestRun,
    paraphrase,
    echoed:
      score >= ECHO_THRESHOLD ||
      longestRun >= RUN_THRESHOLD ||
      paraphrase >= PARAPHRASE_THRESHOLD,
  };
}

/**
 * Judge against EVERY earlier thing the opponent said, and keep the worst.
 *
 * Scoring only against the immediately preceding turn under-reports, and it did:
 * a run where every adjacent pair scored 0.000 still contained one speaker
 * reproducing a clause the other had used two turns earlier, word for word,
 * fifteen words long. The narrow metric called that transcript clean.
 *
 * The echo does not have to be adjacent to be an echo. A model with the whole
 * exchange in its context can copy any part of it, and the accumulating
 * transcript makes older turns just as available as the newest one.
 */
export function judgeEchoAgainstAll(reply: string, priorOpposing: string[]): EchoVerdict {
  let worst: EchoVerdict = { score: 0, longestRun: 0, paraphrase: 0, echoed: false };
  for (const t of priorOpposing) {
    const v = judgeEcho(reply, t);
    // Rank on the flag first, then severity, so a run-only hit is not hidden by
    // a higher-scoring pair that is nonetheless below both thresholds.
    const better =
      (v.echoed && !worst.echoed) ||
      (v.echoed === worst.echoed &&
        (v.score > worst.score ||
          v.longestRun > worst.longestRun ||
          v.paraphrase > worst.paraphrase));
    if (better) worst = v;
  }
  return worst;
}

/**
 * Strip a decorative wrapper the model put around its whole reply.
 *
 * Every reply in the failing transcript came back wrapped in curly quotation
 * marks, on both sides. That is learned from the prompt, which hands the
 * opponent's answer over inside quotes, and it feeds the failure: a quoted reply
 * is stored, quoted again on the way back in, and by the third exchange the
 * boundary between what they said and what I said has no marker left that the
 * model can trust.
 *
 * Only strips when the quotes wrap the ENTIRE reply. A reply that quotes a
 * phrase mid-sentence is doing something legitimate and is left alone.
 */
export function stripWrappingQuotes(text: string): string {
  let t = text.trim();
  for (;;) {
    const first = t[0];
    const last = t[t.length - 1];
    const pair =
      (first === '"' && last === '"') ||
      (first === "“" && last === "”") ||
      (first === "‘" && last === "’");
    if (!pair || t.length < 2) break;
    const inner = t.slice(1, -1);
    // If the inner text closes its own quote early, the outer pair was not a
    // wrapper and removing it would corrupt a legitimate quotation.
    if (/[”"]/.test(inner.slice(0, -1)) && !/[“"]/.test(inner)) break;
    t = inner.trim();
  }
  return t;
}
